"use client";

import * as React from "react";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { XIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { dialogStructureRef } from "./panel-structure";

function Dialog(props: DialogPrimitive.Root.Props) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger(props: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogClose(props: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogPortal({ container, ...props }: DialogPrimitive.Portal.Props) {
  const shellRoot =
    typeof document === "undefined"
      ? undefined
      : (document.querySelector<HTMLElement>(".shell-root") ?? undefined);

  return (
    <DialogPrimitive.Portal
      container={container === undefined ? shellRoot : container}
      data-slot="dialog-portal"
      {...props}
    />
  );
}

function DialogBackdrop({ className, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      className={cn(
        "fixed inset-0 z-50 bg-[rgba(15,25,34,0.32)] duration-150 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0 motion-reduce:animate-none",
        className,
      )}
      data-slot="dialog-backdrop"
      {...props}
    />
  );
}

function DialogContent({
  children,
  className,
  showCloseButton = true,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean;
}) {
  return (
    <DialogPortal>
      <DialogBackdrop />
      <DialogPrimitive.Viewport
        className="fixed inset-0 z-50 grid place-items-center p-4 sm:p-6"
        data-slot="dialog-viewport"
      >
        <DialogPrimitive.Popup
          ref={dialogStructureRef}
          className={cn(
            "relative flex max-h-[calc(100dvh-2rem)] w-full max-w-[480px] flex-col overflow-hidden rounded-xl bg-card text-card-foreground shadow-elevated duration-200 outline-none data-open:animate-in data-open:fade-in-0 data-open:slide-in-from-bottom-2 data-closed:animate-out data-closed:fade-out-0 data-closed:slide-out-to-bottom-2 motion-reduce:animate-none",
            className,
          )}
          data-slot="dialog-content"
          {...props}
        >
          {children}
          {showCloseButton ? (
            <DialogPrimitive.Close
              aria-label="Close"
              className="absolute top-4 right-4 grid size-11 place-items-center rounded-md border border-transparent text-muted-foreground outline-none transition-[color,background-color,box-shadow] duration-150 hover:bg-muted hover:text-foreground focus-visible:shadow-[var(--focus-ring)]"
              data-slot="dialog-close"
            >
              <XIcon aria-hidden="true" className="size-5" />
            </DialogPrimitive.Close>
          ) : null}
        </DialogPrimitive.Popup>
      </DialogPrimitive.Viewport>
    </DialogPortal>
  );
}

/** The fixed top: title, optional subtext, and room for the close button. */
function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("grid shrink-0 gap-1.5 px-6 pt-6 pb-4 pr-16 text-left", className)}
      data-slot="dialog-header"
      {...props}
    />
  );
}

/** The only part of a dialog or sheet that scrolls. */
function DialogBody({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-6 pb-6",
        className,
      )}
      data-slot="dialog-body"
      {...props}
    />
  );
}

/**
 * The fixed bottom. `start` sits on the left and the children on the right; either may hold
 * subtext and any number of buttons. The primary outcome goes last.
 */
function DialogFooter({
  className,
  start,
  children,
  ...props
}: React.ComponentProps<"div"> & { start?: React.ReactNode }) {
  return (
    <div
      className={cn(
        "flex shrink-0 flex-wrap items-center gap-3 border-t border-muted px-6 py-4",
        className,
      )}
      data-slot="dialog-footer"
      {...props}
    >
      {start ? (
        <div className="mr-auto flex flex-wrap items-center gap-3" data-slot="dialog-footer-start">
          {start}
        </div>
      ) : null}
      <div
        className="ml-auto flex flex-wrap items-center justify-end gap-3"
        data-slot="dialog-footer-end"
      >
        {children}
      </div>
    </div>
  );
}

/** Wraps a body and footer in one form so the submit button stays in the footer. */
function DialogForm({ className, ...props }: React.ComponentProps<"form">) {
  return (
    <form
      className={cn("flex min-h-0 flex-1 flex-col", className)}
      data-slot="dialog-form"
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      className={cn(
        "m-0 text-balance font-heading text-2xl leading-tight font-extrabold",
        className,
      )}
      data-slot="dialog-title"
      {...props}
    />
  );
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      className={cn("m-0 text-pretty text-sm leading-relaxed text-muted-foreground", className)}
      data-slot="dialog-description"
      {...props}
    />
  );
}

export {
  Dialog,
  DialogBackdrop,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
